import type { DataCapture } from './types'

const LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone: 'Phone',
  age: 'Age',
  street: 'Street',
  zipcode: 'ZIP code',
  city: 'City',
  country: 'Country',
  age_verification: 'I confirm I meet the age requirement',
  marketing: 'I agree to receive marketing updates',
  gdpr: 'I agree to the GDPR terms'
}

function getParam(name: string): string | null {
  return new URL(location.href).searchParams.get(name)
}

export function injectStyles(root: HTMLElement) {
  const style = document.createElement('style')
  style.textContent = `
  .rlv-wrap { font-family: system-ui, -apple-system, sans-serif; color:#000; }
  .rlv-wrap * { box-sizing: border-box; }
  .rlv-wrap .rlv-form .rlv-h1 { text-transform: uppercase; font-size: 1.25rem; margin: 0 0 12px 0; font-family: system-ui, -apple-system, sans-serif; color:#000; }
  .rlv-wrap .rlv-form .rlv-h2 { text-transform: uppercase; font-size: 1rem; margin: 16px 0 8px 0; font-family: system-ui, -apple-system, sans-serif; color:#000; }
  .rlv-wrap .rlv-form { display:block; }
  .rlv-wrap .rlv-form .rlv-grid { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
  .rlv-wrap .rlv-form .rlv-col-2 { grid-column: 1 / -1; }
  .rlv-wrap .rlv-form .rlv-field { display:flex; flex-direction:column; gap:6px; }
  .rlv-wrap .rlv-form .rlv-label { font-size: 0.9rem; }
  .rlv-wrap .rlv-form .rlv-asterisk { color:#e11d48; margin-left:4px; }
  .rlv-wrap .rlv-form .rlv-input, .rlv-select, .rlv-textarea { border:2px solid #000; background:#fff; color:#000; padding:10px; width:100%; outline:none; }
  .rlv-wrap .rlv-form .rlv-checkbox-row { display:flex; align-items:flex-start; gap:8px; margin: 6px 0; }
  .rlv-wrap .rlv-form .rlv-errorbox { border:2px solid #f00; background:#ffe0e0; color:#000; padding:10px; margin:12px 0; display:none; }
  .rlv-wrap .rlv-success { border:2px solid #0f0; background:#cdffcd; color:#000; padding:12px; }
  .rlv-wrap .rlv-form .rlv-actions { margin-top: 12px; }
  .rlv-wrap .rlv-form .rlv-btn { background:#000; color:#fff; border:0; padding:12px 16px; cursor:pointer; }
  .rlv-wrap .rlv-form .rlv-spinner { display:inline-block; width:18px; height:18px; border:2px solid #000; border-top-color: transparent; animation: rlv-spin 0.9s linear infinite; margin-left:8px; }
  @keyframes rlv-spin { to { transform: rotate(360deg); } }
  @media (max-width: 640px) { .rlv-grid { grid-template-columns: 1fr; } }
  `
  root.appendChild(style)
}

function addTextInput(grid: HTMLElement, name: string, required: boolean, fullWidth = false, type: string = 'text') {
  const wrap = document.createElement('div')
  wrap.className = `rlv-field ${fullWidth ? 'rlv-col-2' : ''}`.trim()
  const label = document.createElement('label')
  label.className = 'rlv-label'
  label.setAttribute('for', `rlv-${name}`)
  label.textContent = LABELS[name] || name
  if (required) {
    const star = document.createElement('span')
    star.className = 'rlv-asterisk'
    star.textContent = '*'
    label.appendChild(star)
  }
  const input = document.createElement('input')
  input.className = 'rlv-input'
  input.id = `rlv-${name}`
  input.name = name
  input.type = type
  if (required) input.required = true
  wrap.append(label, input)
  grid.appendChild(wrap)
}

function addSelect(grid: HTMLElement, name: string, required: boolean, fullWidth = false) {
  const wrap = document.createElement('div')
  wrap.className = `rlv-field ${fullWidth ? 'rlv-col-2' : ''}`.trim()
  const label = document.createElement('label')
  label.className = 'rlv-label'
  label.setAttribute('for', `rlv-${name}`)
  label.textContent = LABELS[name] || name
  if (required) {
    const star = document.createElement('span')
    star.className = 'rlv-asterisk'
    star.textContent = '*'
    label.appendChild(star)
  }
  const sel = document.createElement('select')
  sel.className = 'rlv-select'
  sel.id = `rlv-${name}`
  sel.name = name
  if (required) sel.required = true
  const ph = document.createElement('option')
  ph.value = ''
  ph.textContent = 'Please select'
  sel.appendChild(ph)
  const countries = [
    'Germany',
    'Austria',
    'Switzerland',
    'France',
    'Italy',
    'Spain',
    'Netherlands',
    'Belgium',
    'Sweden',
    'Denmark',
    'Finland'
  ]
  countries.forEach((c) => {
    const o = document.createElement('option')
    o.value = c
    o.textContent = c
    sel.appendChild(o)
  })
  wrap.append(label, sel)
  grid.appendChild(wrap)
}

export function renderFormFromDataCapture(container: HTMLElement, dc: DataCapture) {
  container.classList.add('rlv-wrap')

  const required = Object.keys(dc).filter((k) => (dc as any)[k] === 'required')
  const optional = Object.keys(dc).filter((k) => (dc as any)[k] === 'optional')

  const form = document.createElement('form')
  form.className = 'rlv-form'
  form.noValidate = true

  // Add event id to the form
  const eventIdInput = document.createElement('input')
  eventIdInput.type = 'hidden'
  eventIdInput.name = 'eventId'
  eventIdInput.value = container.dataset.eventId!
  form.appendChild(eventIdInput)

  const code = getParam('code')
  if (code) {
    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = 'code'
    hidden.value = code
    form.appendChild(hidden)
  }

  const h1 = document.createElement('h1')
  h1.className = 'rlv-h1'
  h1.textContent = 'Enter your information'
  form.appendChild(h1)

  const grid = document.createElement('div')
  grid.className = 'rlv-grid'

  // Pair first/last name if both required
  const hasFirst = required.includes('first_name')
  const hasLast = required.includes('last_name')
  if (hasFirst && hasLast) {
    addTextInput(grid, 'first_name', true, false)
    addTextInput(grid, 'last_name', true, false)
    required.splice(required.indexOf('first_name'), 1)
    required.splice(required.indexOf('last_name'), 1)
  }

  // Street full width if required
  if (required.includes('street')) {
    addTextInput(grid, 'street', true, true)
    required.splice(required.indexOf('street'), 1)
  }

  // Add remaining required as full width, with special casing
  for (const k of [...required]) {
    if (k === 'zipcode' || k === 'city') continue // paired later
    if (k === 'country') continue // after city
    if (k === 'email') {
      addTextInput(grid, k, true, true, 'email')
      continue
    }
    if (k === 'phone') {
      addTextInput(grid, k, true, true, 'tel')
      continue
    }
    if (k === 'age') {
      addTextInput(grid, k, true, true, 'number')
      continue
    }
    addTextInput(grid, k, true, true)
  }

  // zipcode next to city (required)
  const hasZip = required.includes('zipcode')
  const hasCity = required.includes('city')
  if (hasZip) addTextInput(grid, 'zipcode', true, false)
  if (hasCity) addTextInput(grid, 'city', true, false)

  // country after city, full width
  if ((dc as any).country === 'required') {
    addSelect(grid, 'country', true, true)
  }

  form.appendChild(grid)

  // Optional section (except checkboxes)
  const optFields = optional.filter((k) => !['age_verification', 'marketing', 'gdpr'].includes(k))
  if (optFields.length) {
    const h2 = document.createElement('h2')
    h2.className = 'rlv-h2'
    h2.textContent = 'Optional: Tell us more about you'
    form.appendChild(h2)
    const grid2 = document.createElement('div')
    grid2.className = 'rlv-grid'

    const oFirst = optFields.includes('first_name')
    const oLast = optFields.includes('last_name')
    if (oFirst && oLast) {
      addTextInput(grid2, 'first_name', false, false)
      addTextInput(grid2, 'last_name', false, false)
    }

    if (optFields.includes('street')) addTextInput(grid2, 'street', false, true)

    const oZip = optFields.includes('zipcode')
    const oCity = optFields.includes('city')
    if (oZip) addTextInput(grid2, 'zipcode', false, false)
    if (oCity) addTextInput(grid2, 'city', false, false)

    if ((dc as any).country === 'optional') addSelect(grid2, 'country', false, true)

    for (const k of optFields) {
      if (['first_name', 'last_name', 'street', 'zipcode', 'city', 'country'].includes(k)) continue
      if (k === 'email') {
        addTextInput(grid2, k, false, false, 'email')
        continue
      }
      if (k === 'phone') {
        addTextInput(grid2, k, false, false, 'tel')
        continue
      }
      if (k === 'age') {
        addTextInput(grid2, k, false, false, 'number')
        continue
      }
      addTextInput(grid2, k, false, false)
    }

    form.appendChild(grid2)
  }

  // Checkboxes before submit
  for (const name of ['age_verification', 'marketing', 'gdpr'] as const) {
    const flag = (dc as any)[name]
    if (!flag) continue
    const row = document.createElement('label')
    row.className = 'rlv-checkbox-row'
    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.name = name
    cb.id = `rlv-${name}`
    if (flag === 'required') cb.required = true
    const text = document.createElement('span')
    text.textContent = LABELS[name] || name
    if (flag === 'required') {
      const star = document.createElement('span')
      star.className = 'rlv-asterisk'
      star.textContent = '*'
      text.appendChild(star)
    }
    row.append(cb, text)
    form.appendChild(row)
  }

  // Error box (hidden by default)
  const err = document.createElement('div')
  err.className = 'rlv-errorbox'
  err.setAttribute('role', 'alert')
  err.id = 'rlv-error'
  err.textContent = 'There was an error. Please try again.'
  form.appendChild(err)

  // Actions
  const actions = document.createElement('div')
  actions.className = 'rlv-actions'
  const submit = document.createElement('button')
  submit.type = 'submit'
  submit.className = 'rlv-btn'
  submit.textContent = 'Submit'
  actions.appendChild(submit)
  form.appendChild(actions)

  container.replaceChildren(form)
  injectStyles(container)
}

export function renderLoadError(container: HTMLElement, message = "We couldn't load the form right now.") {
  container.classList.add('rlv-wrap')
  injectStyles(container)
  const box = document.createElement('div')
  box.className = 'rlv-errorbox'
  box.style.display = 'block'
  box.textContent = message
  container.replaceChildren(box)
}
